import React, { useEffect, useRef } from 'react';

/**
 * InlineTextEditor — absolute-positioned textarea that sits on top of the
 * selected text element on the canvas. Save on blur / Enter, cancel on Esc.
 *
 * Props:
 *   engine             — the template engine (for element lookup + updateElement)
 *   canvasWrapperRef   — ref to the canvas wrapper DOM node (for positioning)
 *
 * Mounts only when engine.inlineEditingId is set.
 */
export const InlineTextEditor = ({ engine, canvasWrapperRef }) => {
  const ref = useRef(null);
  const editingId = engine.inlineEditingId;
  const element = editingId ? engine.elements.find(e => e.id === editingId) : null;
  const isText = element && element.type === 'text';

  useEffect(() => {
    if (!isText || !ref.current) return;
    ref.current.focus();
    ref.current.select();
  }, [isText, editingId]);

  if (!isText) return null;

  // Find the konva stage container so we can position absolutely relative to it.
  const stageNode = engine.stageRef?.current;
  const container = stageNode?.container();
  const wrapper = canvasWrapperRef?.current;
  if (!container || !wrapper) return null;

  const wrapRect = wrapper.getBoundingClientRect();
  const stageRect = container.getBoundingClientRect();

  // Offset of the konva stage inside the wrapper (in CSS pixels).
  const offsetLeft = stageRect.left - wrapRect.left;
  const offsetTop = stageRect.top - wrapRect.top;

  const scale = engine.scale || 1;
  const left = offsetLeft + (element.x || 0) * scale;
  const top = offsetTop + (element.y || 0) * scale;
  const width = Math.max(80, (element.width || 200) * scale);
  const fontSize = Math.max(12, (element.fontSize || 24) * scale * 0.95);

  const commit = (next) => {
    if (next !== undefined && next !== element.text) {
      engine.updateElement({ ...element, text: next });
    }
    engine.setInlineEditingId(null);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      engine.setInlineEditingId(null);
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commit(e.target.value);
    }
  };

  return (
    <textarea
      ref={ref}
      defaultValue={element.text || ''}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={onKeyDown}
      style={{
        position: 'absolute',
        left,
        top,
        width,
        minHeight: fontSize * 1.2 + 4,
        padding: '2px 4px',
        margin: 0,
        zIndex: 50,
        fontSize,
        lineHeight: 1.1,
        fontFamily: element.fontFamily || 'inherit',
        fontWeight: element.fontStyle?.includes('bold') ? 700 : 400,
        fontStyle: element.fontStyle?.includes('italic') ? 'italic' : 'normal',
        color: element.fill || '#000',
        textAlign: element.align || 'left',
        background: 'rgba(255,255,255,0.95)',
        border: '2px solid #0B0C10',
        outline: 'none',
        borderRadius: 4,
        resize: 'none',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
      }}
      data-testid="inline-text-editor"
    />
  );
};

export default InlineTextEditor;
